import type { ApiError } from '../src/api.js';
import type { Skill, Fighter, Stats } from '../src/content/fighters.js';
import type { GeneralSkill } from '../src/content/general.js';
import type { StatusKind } from '../src/game/battle.js';

/** Markup that is safe to put on the page. Only `h` and this module can make one. */
class SafeHtml {
  readonly #html: string;
  constructor(html: string) {
    this.#html = html;
  }
  toString(): string {
    return this.#html;
  }
}
export type Html = SafeHtml;
type HtmlValue = Html | string | number | false | null | undefined | readonly HtmlValue[];

/** A failed request to our own API, carrying the server's error message and the HTTP status. */
class RequestError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
  }
}

/** Our own server's JSON, typed by the definitions it shares with us in src/api.ts. */
const readJson = async <T>(res: Response): Promise<T> => res.json();

/** JSON request to our own API. Throws a `RequestError` with the server's error message on failure. */
const api = async <T>(
  path: string,
  opts: { method?: 'GET' | 'POST' | 'PUT' | 'DELETE'; body?: object } = {},
): Promise<T> => {
  const res = await fetch(path, {
    method: opts.method,
    headers: { 'Content-Type': 'application/json' },
    body: opts.body ? JSON.stringify(opts.body) : undefined,
  });
  if (!res.ok) {
    const err = await readJson<Partial<ApiError>>(res).catch(() => null);
    throw new RequestError(err?.error || `Request failed (${res.status})`, res.status);
  }
  return readJson<T>(res);
};

const ESCAPES: Record<string, string> = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
const esc = (s: string): string => s.replace(/[&<>"']/g, c => ESCAPES[c]);

const fragment = (val: HtmlValue): string => {
  if (val instanceof SafeHtml) return val.toString();
  if (Array.isArray(val)) return val.map(fragment).join('');
  if (val === null || val === undefined || val === false) return '';
  return esc(String(val));
};

/** Template tag for markup. Interpolated text is escaped; `h` fragments and arrays of them are inserted as they are. */
const h = (strings: TemplateStringsArray, ...vals: HtmlValue[]): Html =>
  new SafeHtml(strings.reduce((out, s, i) => out + s + (i < vals.length ? fragment(vals[i]) : ''), ''));

const setHtml = (target: Element, html: Html): void => {
  target.innerHTML = html.toString();
};

/** Finds a required element, checked to be of the given type. */
const elOf = <T extends Element>(type: new () => T, selector: string, root: ParentNode = document): T => {
  const found = root.querySelector(selector);
  if (!(found instanceof type)) throw new Error(`Missing element: ${selector}`);
  return found;
};

const el = (selector: string, root: ParentNode = document): HTMLElement => elOf(HTMLElement, selector, root);

const toast = (msg: string, isError = false): void => {
  const div = document.createElement('div');
  div.className = 'toast' + (isError ? ' error' : '');
  div.textContent = msg;
  document.body.append(div);
  setTimeout(() => div.remove(), 3200);
};

/** Runs an action and shows any failure as an error toast. */
const attempt = async (action: () => Promise<void>): Promise<void> => {
  try {
    await action();
  } catch (err) {
    toast(err instanceof Error ? err.message : String(err), true);
  }
};

const triggerLabel: Record<Skill['on'] | GeneralSkill['on'], string> = {
  passive: 'Passive',
  general: 'General',
  start: 'At the start',
  lowHp: 'Low HP',
  allyDown: 'Ally down',
  enemyDown: 'Enemy down',
  takedown: 'Takedown',
  teamTakedown: 'Team takedown',
  death: 'On death',
};

/** `tip` describes a live condition of strength `n`; `about` describes the condition in general. */
const status: Record<
  StatusKind,
  { icon: string; about: string; tip: (n: number) => string; text?: (n: number) => string }
> = {
  taunt: {
    icon: '🎯',
    about: 'Taunt: enemies attacking her line must target her',
    tip: () => 'Taunt: enemies attacking her line must target her',
  },
  burn: {
    icon: '🔥',
    about: 'Burn: takes damage at the start of each turn, ignoring DEF, until it fades. Burns stack.',
    tip: n => `Burning: takes ${n} at the start of each turn`,
    text: n => `${n}`,
  },
  chill: {
    icon: '❄️',
    about: 'Chill: −6% ATK and DEF per stack, up to 10 stacks',
    tip: n => `Chilled: −${n * 6}% ATK and DEF`,
    text: n => `×${n}`,
  },
  stun: { icon: '💫', about: 'Stun: skips the next turn', tip: () => 'Stunned: skips the next turn' },
  confuse: {
    icon: '🌀',
    about: 'Confuse: the next attack goes at their own team, maybe themselves',
    tip: () => 'Confused: the next attack goes at their own team, maybe themselves',
  },
  hidden: { icon: '👻', about: "Vanish: can't be targeted", tip: () => "Vanished: can't be targeted" },
  hacked: {
    icon: '🔒',
    about: 'Hack: passives are switched off',
    tip: () => 'Hacked: passives are switched off',
  },
  atkUp: { icon: '⚔️', about: 'ATK up', tip: n => `ATK +${n}%`, text: n => `+${n}%` },
  defUp: {
    icon: '🪨',
    about: 'DEF up or down',
    tip: n => `DEF ${n > 0 ? '+' : '−'}${Math.abs(n)}%`,
    text: n => `${n > 0 ? '+' : '−'}${Math.abs(n)}%`,
  },
  marked: {
    icon: '📍',
    about: 'Marked: the enemy team attacks her first whenever they can reach her',
    tip: () => 'Marked: the enemy team attacks her first whenever they can reach her',
  },
  primed: { icon: '💎', about: 'Next attack is a guaranteed crit', tip: () => 'Next attack is a guaranteed crit' },
  sweep: {
    icon: '⚓',
    about: 'Next attack hits every enemy in reach',
    tip: () => 'Next attack hits every enemy in reach',
  },
};
const statusKinds = Object.keys(status).filter((k): k is StatusKind => Object.hasOwn(status, k));

const CONDITION_WORDS: Record<string, StatusKind> = {
  burn: 'burn',
  burns: 'burn',
  chill: 'chill',
  chills: 'chill',
  stun: 'stun',
  stuns: 'stun',
  confuse: 'confuse',
  hacks: 'hacked',
  vanishes: 'hidden',
  marks: 'marked',
};
const CONDITION_PATTERN = new RegExp(`\\b(${Object.keys(CONDITION_WORDS).join('|')})\\b`, 'gi');

/** Escaped skill text with the battle viewer's icon in front of every condition it names, explained on hover. */
const skillText = (text: string): Html =>
  new SafeHtml(
    esc(text).replace(CONDITION_PATTERN, word => {
      const kind = CONDITION_WORDS[word.toLowerCase()];
      return kind
        ? h`<span class="condition" data-tip="${status[kind].about}"><span class="condition-icon" aria-hidden="true">${status[kind].icon}</span>${word}</span>`.toString()
        : word;
    }),
  );

/** A skill as a fighter holds it right now. `off` skills are listed but don't work; `note` says why or where from. */
export interface SkillRow {
  skill: Skill | GeneralSkill;
  off?: boolean;
  note?: string;
}

/** What a skill does; a general skill leads with its boost. */
const skillBody = (s: Skill | GeneralSkill): Html =>
  s.on === 'general' ? h`<b class="boost">${s.effect}</b> ${skillText(s.text)}` : skillText(s.text);

const skillsHtml = (rows: SkillRow[]): Html =>
  h`${rows.map(
    ({ skill: s, off, note }) =>
      h`<div class="ability${off ? ' off' : ''}${s.on === 'general' ? ' general' : ''}"><b>${s.name}</b> <span class="trigger">${triggerLabel[s.on]}</span>${note ? h` <span class="skill-note">${note}</span>` : ''}<br>${skillBody(s)}</div>`,
  )}`;

const percent = (x: number) => `${Math.round(x * 100)}%`;

/** Her base crit chance, and what general skills make of it. Kept small: crits are mostly her skills' job. */
const critHtml = (w: Fighter, boosted: Stats = w.stats): Html => {
  const gain = boosted.crit - w.stats.crit;
  return h`<div class="crit-line">Crit <b class="${gain > 0 ? 'boosted' : ''}">${percent(boosted.crit)}</b>${gain > 0 ? h` <span class="boost">+${percent(gain)}</span>` : ''}</div>`;
};

const fighterDetails = (w: Fighter, rows: SkillRow[] = w.skills.map(skill => ({ skill }))): Html =>
  h`<div class="stats">
      <div><b>${w.stats.hp}</b>HP</div><div><b>${w.stats.atk}</b>ATK</div>
      <div><b>${w.stats.def}</b>DEF</div><div><b>${w.stats.spd}</b>SPD</div>
    </div>
    ${skillsHtml(rows)}`;

const STAT_KEYS = ['hp', 'atk', 'def', 'spd'] as const satisfies (keyof Stats)[];

/** Where a fighter's stat sits in the roster. Within 5% of the average counts as `even`. */
const standing = (w: Fighter, roster: Fighter[], key: keyof Stats) => {
  const values = roster.map(f => f.stats[key]);
  const value = w.stats[key];
  const avg = values.reduce((a, b) => a + b, 0) / values.length;
  const diff = Math.round(((value - avg) / avg) * 100);
  return {
    value,
    avg,
    min: Math.min(...values),
    max: Math.max(...values),
    rank: values.filter(v => v > value).length + 1,
    of: values.length,
    diff,
    trend: Math.abs(diff) < 5 ? 'even' : diff > 0 ? 'up' : 'down',
  };
};

const pct = (n: number): string => `${(n * 100).toFixed(1)}%`;

/**
 * Each stat as a slim bar against the roster, with the roster average marked and the difference to it spelled out.
 * With `boosted` stats, what general skills add or take away shows on the bar and replaces the difference.
 */
const statBars = (w: Fighter, roster: Fighter[], boosted: Stats = w.stats): Html =>
  h`<div class="stat-bars">${STAT_KEYS.map(key => {
    const s = standing(w, roster, key);
    const now = boosted[key];
    const change = now - s.value;
    const scale = Math.max(s.max, now);
    const tip = `Roster average ${s.avg.toFixed(1)} (the mark on the bar), range ${s.min}–${s.max}. #${s.rank} of ${s.of}.${change ? ` ${change > 0 ? '+' : '−'}${Math.abs(change)} from general skills.` : ''}`;
    const diff = change
      ? `${change > 0 ? '+' : '−'}${Math.abs(change)}`
      : s.trend === 'even'
        ? 'avg'
        : `${s.diff > 0 ? '+' : '−'}${Math.abs(s.diff)}%`;
    return h`<div class="stat-bar ${s.trend} ${change > 0 ? 'boosted' : change < 0 ? 'lowered' : ''}" data-tip="${tip}">
      <span class="key">${key.toUpperCase()}</span>
      <svg class="track" aria-hidden="true"><rect class="fill" width="${pct(Math.min(s.value, now) / scale)}" height="100%" rx="2.5"/>${change ? h`<rect class="change" x="${pct(Math.min(s.value, now) / scale)}" width="${pct(Math.abs(change) / scale)}" height="100%" rx="2.5"/>` : ''}<rect class="avg" x="${pct(s.avg / scale)}" y="-30%" width="2" height="160%"/></svg>
      <b>${now}</b>
      <span class="diff">${diff}</span>
    </div>`;
  })}</div>`;

/** The reigning champion's crown, counting back-to-back wins. */
const crownHtml = ({ streak }: { streak: number }): Html =>
  h`<span class="crown" title="${streak > 1 ? `Reigning champion, ${streak} tournaments in a row` : 'Reigning champion'}">👑${streak > 1 ? h`<span class="crown-streak">×${streak}</span>` : ''}</span>`;

export const Ui = {
  RequestError,
  api,
  h,
  setHtml,
  el,
  elOf,
  toast,
  attempt,
  triggerLabel,
  status,
  statusKinds,
  skillText,
  skillsHtml,
  fighterDetails,
  STAT_KEYS,
  standing,
  statBars,
  critHtml,
  crownHtml,
};
